import {
  ICourse,
  ICourseCertificate,
  ICourseCertificateDesign,
  ICourseRequirement,
  ICreateCourse,
  ICreateCourseCertificate,
  ICreateCourseRequirement,
  UserCourseCompletionStats,
} from '@/types/courses';
import { Delete, FetchError, Get, Patch, Post, Put } from './helpers';

export function GetCourses(channel: number, page = 1, limit = 10000) {
  return Get<ICourse[]>({
    path: `/v4/admin/courses/${channel}?page=${page}&limit=${limit}`,
  });
}

export function GetFullCourse(channel: number, courseUuid: string) {
  return Get<Required<ICourse>>({
    path: `/v4/admin/courses/${channel}/${courseUuid}`,
  });
}

export function GetCourseForEvent(channel: number, eventUuid: string) {
  return Get<Required<ICourse>>({
    path: `/v4/admin/courses/${channel}/event/${eventUuid}`,
  });
}

export function CreateCourse(channel: number, course: ICreateCourse) {
  return Post<Required<ICourse>>({
    path: `/v4/admin/courses/${channel}`,
    body: course,
  });
}

export function DuplicateCourse(channel: number, eventId: number, courseUuid: string) {
  return Post<Required<ICourse>>({
    path: `/v4/admin/courses/${channel}/course/duplicate/${courseUuid}`,
    body: { eventId },
  });
}

export function UpdateCourse(channel: number, courseUuid: string, course: ICreateCourse, signal?: AbortSignal) {
  return Patch<ICreateCourse>({
    path: `/v4/admin/courses/${channel}/${courseUuid}`,
    body: course,
    signal,
  });
}

export function DeleteCourse(channel: number, courseUuid: string) {
  return Delete({
    path: `/v4/admin/courses/${channel}/${courseUuid}`,
    body: {},
    ignoreResponse: true,
  });
}

export function GetCertificateForCourseUuid(channel: number, courseUuid: string) {
  return Get<Required<ICourseCertificate>>({
    path: `/v4/admin/courses/${channel}/${courseUuid}/certificate`,
  });
}

export function GetCourseCertificate(channel: number, certificateUuid: string) {
  return Get<Required<ICourseCertificate>>({
    path: `/v4/admin/courses/${channel}/certificate/${certificateUuid}`,
  });
}

export function CreateCourseCertificate(channel: number, certificate: ICreateCourseCertificate) {
  return Post<ICourseCertificate>({
    path: `/v4/admin/courses/${channel}/certificate`,
    body: certificate,
  });
}

export function UpdateCourseCertificate(
  channel: number,
  certificateUuid: string,
  certificate: ICreateCourseCertificate
) {
  return Patch<ICourseCertificate>({
    path: `/v4/admin/courses/${channel}/certificate/${certificateUuid}`,
    body: certificate,
  });
}

export function DeleteCourseCertificate(channel: number, certificateUuid: string) {
  return Delete({
    path: `/v4/admin/courses/${channel}/certificate/${certificateUuid}`,
    body: {},
  });
}

export function GetCourseRequirements(channel: number, courseUuid: string) {
  return Get<ICourseRequirement[]>({
    path: `/v4/admin/courses/${channel}/${courseUuid}/requirements`,
  });
}

export function CreateCourseRequirement(channel: number, courseUuid: string, requirement: ICreateCourseRequirement) {
  return Post<ICourseRequirement>({
    path: `/v4/admin/courses/${channel}/${courseUuid}/requirements`,
    body: requirement,
  });
}

export function UpdateCourseRequirement(
  channel: number,
  courseUuid: string,
  requirementUuid: string,
  requirement: ICreateCourseRequirement
) {
  return Patch<ICourseRequirement>({
    path: `/v4/admin/courses/${channel}/${courseUuid}/requirements/${requirementUuid}`,
    body: requirement,
  });
}

export function DeleteCourseRequirement(channel: number, courseUuid: string, requirementUuid: string) {
  return Delete({
    path: `/v4/admin/courses/${channel}/${courseUuid}/requirements/${requirementUuid}`,
    body: {},
    ignoreResponse: true,
  });
}

export function GetCourseAttendee(token: string, eventUuid: string) {
  return Get<ICourse>({
    path: `/e3-get-course/${eventUuid}`,
    headers: {
      'Bl-Profile-Token': token,
    },
  });
}

export function SendPulseCheckReceipt(token: string, pulseCheckId: number) {
  return Post<ICourse>({
    path: `/scheduled-pulse-check-receipt`,
    body: { pulseCheckId, token },
  }).catch((e: unknown) => {
    if (e instanceof FetchError && e.response.status === 200 && e.message.startsWith('<?xml')) {
      return;
    } else {
      throw e;
    }
  });
}

export function SendPulseCheckAcknowledgement(token: string, pulseCheckId: number) {
  return Post<ICourse>({
    path: `/scheduled-pulse-check-acknowledgement`,
    body: { pulseCheckId, token },
  }).catch((e: unknown) => {
    if (e instanceof FetchError && e.response.status === 200 && e.message.startsWith('<?xml')) {
      return;
    } else {
      throw e;
    }
  });
}

export function GetCourseCertificateDesigns(channel: number) {
  return Get<ICourseCertificateDesign[]>({
    path: `/v4/admin/courses/${channel}/certificate-designs`,
  });
}

export function GetCanonicalCoursePassState(eventUuid: string) {
  const params = new URLSearchParams();
  if (localStorage.getItem('NO-CACHE')) {
    params.set('no-cache', 'true');
  }
  return Get<UserCourseCompletionStats>({
    path: `/e3-get-course-completion/${eventUuid}?${params.toString()}`,
  });
}

export function ForceCompleteCourse(
  channelId: number,
  courseUuid: string,
  blProfileId: number
): Promise<{
  completed: boolean;
  certificateLocation: string | null;
}> {
  return Put({
    path: `/v4/admin/courses/${channelId}/${courseUuid}/completions`,
    body: { blProfileId },
  });
}

export function CheckCertificateGenerating(channelId: number, courseUuid: string, blProfileId: number) {
  const params = new URLSearchParams({ blProfileId: String(blProfileId) });
  return Get<{
    generating: boolean;
  }>({
    path: `/v4/admin/courses/${channelId}/${courseUuid}/certificate-generating?${params.toString()}`,
  });
}

export function GetCourseCertDownloadForUser(channelId: number, courseUuid: string, blProfileId: number) {
  return Get<{ url: string }>({
    path: `/v4/admin/courses/${channelId}/${courseUuid}/certificate-download/${blProfileId}`,
  });
}

export const GetChannelTracks = async (channel: number, token?: string, include?: string): Promise<string[]> => {
  const params = new URLSearchParams();
  if (include) {
    params.set('include', include);
  }
  return Get({
    path: `/v3/admin/events/tracks/${channel}?${params.toString()}`,
  });
};
